/**
 * KraaftJobPanel — inline card on the WO detail page showing every mirrored
 * Kraaft record linked to this WO. The crew's field photos live in Kraaft
 * (their public API doesn't expose photo bytes), so we surface the count
 * plus a one-tap "Open in Kraaft" deeplink instead of a manual copy-paste.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Notepad, ArrowUpRight, MapPin, CheckCircle } from "@phosphor-icons/react";

export default function KraaftJobPanel({ jobId }) {
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    api.get(`/kraaft/records/for-job/${jobId}`)
      .then(r => { if (alive) { setRecords(r.data?.records || []); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [jobId]);

  if (!loaded || records.length === 0) return null;

  // Count real Kraaft photos (parcelIds) — parcel bytes aren't downloadable,
  // but the count is authoritative from the record's `photos.value` array.
  const totalPhotos = records.reduce(
    (a, r) => a + (r.kraaft_parcel_count || (r.photos?.length || 0)),
    0
  );

  return (
    <div className="mt-4 rounded-md border-2 border-fuchsia-300 bg-fuchsia-50/40 p-3" data-testid="kraaft-job-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="h-6 w-6 rounded bg-[#0f172a] text-white text-[10px] font-black grid place-content-center">K</span>
        <div className="text-xs font-black uppercase tracking-widest text-slate-800">From Kraaft</div>
        <span className="text-[10px] text-slate-500">
          · {records.length} record{records.length === 1 ? "" : "s"} · 📸 {totalPhotos} photo{totalPhotos === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-3">
        {records.map(r => (
          <KraaftRecordCard key={r._id || r.record_id} r={r}/>
        ))}
      </div>
    </div>
  );
}

function KraaftRecordCard({ r }) {
  const done = !!(((((r.raw || {}).properties || {}).status || {})).value);
  const parcelCount = r.kraaft_parcel_count || (r.photos?.length || 0);
  const geo = r.kraaft_geo;
  const mapsUrl = geo ? `https://www.google.com/maps/search/?api=1&query=${geo.lat},${geo.lng}` : null;

  return (
    <div className="border border-fuchsia-200 bg-white rounded p-2" data-testid={`kraaft-rec-${r.record_id}`}>
      {r.title && (
        <div className="flex items-start gap-1.5">
          {done && <CheckCircle weight="fill" size={14} className="text-emerald-600 mt-0.5 flex-shrink-0"/>}
          <div className="text-sm font-semibold flex-1">{r.title}</div>
        </div>
      )}
      {r.notes && (
        <div className="mt-1 text-xs text-slate-700 whitespace-pre-wrap flex items-start gap-1">
          <Notepad weight="bold" size={12} className="text-fuchsia-600 mt-0.5 shrink-0"/>
          <span>{r.notes}</span>
        </div>
      )}
      {/* Any URL-based photos we managed to scavenge — usually empty for Kraaft public API */}
      {(r.photos || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {r.photos.map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block h-16 w-16 border border-slate-200 overflow-hidden rounded">
              <img src={p.url} alt={p.name || `Kraaft ${i}`} className="h-full w-full object-cover"/>
            </a>
          ))}
        </div>
      )}
      {/* Kraaft parcel photos — can't embed, so deeplink into the app */}
      {parcelCount > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-800 px-2 py-1 rounded">
            <Camera weight="fill" size={12}/> {parcelCount} photo{parcelCount === 1 ? "" : "s"} in Kraaft
          </span>
          {r.kraaft_deeplink && (
            <a
              href={r.kraaft_deeplink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`kraaft-open-${r.record_id}`}
              className="inline-flex items-center gap-1 h-7 px-2 border-2 border-slate-900 bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-black uppercase tracking-widest text-[10px] rounded shadow-[2px_2px_0px_#0f172a]">
              <ArrowUpRight weight="bold" size={12}/> Open in Kraaft
            </a>
          )}
          {mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`kraaft-map-${r.record_id}`}
              className="inline-flex items-center gap-1 h-7 px-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-black uppercase tracking-widest text-[10px] rounded">
              <MapPin weight="fill" size={11}/> Photo pin
            </a>
          )}
        </div>
      )}
      <div className="mt-1 text-[10px] text-slate-400 inline-flex items-center gap-1">
        <Camera weight="bold" size={10}/> Mirrored {r.mirrored_at ? new Date(r.mirrored_at).toLocaleString() : ""}
        {r.match_reason && <> · via {r.match_reason.replace(/_/g, " ")}</>}
      </div>
    </div>
  );
}
